import { BASE_URL } from "@/config";
import axios from "axios";
import { CalendarEvent, CalendarLocation, CalendarPartner, CalendarVehicle } from "../calendar/calendar-types";
import { getAuthHeader } from "@/lib/utils";
import { toast } from "sonner";

const getReservationStates = () => {
    return [
        { value: 'pending', label: 'Pending', color: 'amber' },
        { value: 'approved', label: 'Approved', color: 'emerald' },
        { value: 'rejected', label: 'Rejected', color: 'red' },
    ];
}

const getLocations = async () => {
    let locations: CalendarLocation[] = [];
    var response = await axios.get(`${BASE_URL}/location/device`, {
        headers: getAuthHeader(),
        data: {
            start: 1,
            end: 10000,
        }
    });

    if (Array.isArray(response?.data)) {
        locations = response.data.map((location: any) => ({
            id: location.id,
            name: `${location.address}, ${location.city}, ${location.state}`,
        }));
    }
    else {
        console.error("Unexpected getLocations format:", response);
    }

    return locations;
};

const getCommissionPartners = async () => {

    let partners: CalendarPartner[] = [];
    const response = await axios.get(`${BASE_URL}/referral-agents`, {
        headers: getAuthHeader(),
    });

    if (Array.isArray(response?.data)) {
        partners = response.data.map((partner: any) => ({
            id: partner.id,
            name: `${partner.name} (${partner.percentage_commission}%)`,
            commission: partner.percentage_commission,
        }));
    }
    else {
        console.error("Unexpected getCommissionPartners format:", response);
    }

    return partners;
};

const getVehicles = async () => {
    let vehicles: CalendarVehicle[] = [];

    const response = await axios.get(`${BASE_URL}/vehicles`, {
        headers: getAuthHeader(),
    });

    if (Array.isArray(response?.data)) {
        vehicles = response.data.map((vehicle: any) => ({
            id: vehicle.id,
            name: (`${vehicle.make.name} - ${vehicle.license_plate} `) + (vehicle.nickname ? `(${vehicle.nickname})` : ''),
        }));
    }
    else {
        console.error("Unexpected response format:", response);
    }

    return vehicles;
};

const getAvailableVehicles = async (pickup: Date, dropoff: Date) => {
    let vehicles: CalendarVehicle[] = [];

    const pickup_date = pickup.toISOString();
    const dropoff_date = dropoff.toISOString();

    const response = await axios.get(`${BASE_URL}/reservations/rental/get-availability/${pickup_date}/${dropoff_date}`, {
        headers: getAuthHeader(),
    });

    if (Array.isArray(response?.data)) {
        vehicles = response.data.map((vehicle: any) => ({
            id: vehicle.id,
            name: (`${vehicle.make.name} - ${vehicle.license_plate} `) + (vehicle.nickname ? `(${vehicle.nickname})` : ''),
            totalPrice: vehicle.totalPrice,
        }));
    }
    else {
        console.error("Unexpected response format:", response);
    }

    return vehicles;
};

const getReservations = async (start: Date, end: Date) => {
    let events: CalendarEvent[] = [];

    const startDate = start.toISOString();
    const endDate = end.toISOString();

    const response = await axios.get(`${BASE_URL}/reservations/rental/find-reservations/${startDate}/${endDate}`, {
        headers: {
            'Authorization': `Bearer ${localStorage.getItem("access_token")}`,
        },
    });

    if (Array.isArray(response?.data)) {
        events = response.data.map((reservation: any) => ({
            id: reservation.id.toString(),
            title: (`${reservation.vehicle?.make?.name} - ${reservation.vehicle?.license_plate} `) + (reservation.vehicle?.nickname ? `(${reservation.vehicle.nickname})` : ''),
            start: new Date(reservation.pickup_date),
            end: new Date(reservation.dropoff_date),
            color: getReservationStates().find(x => x.value === reservation.status)?.color || "yellow",

            // reservation details
            vehicleId: reservation.vehicle_id,
            pickup: (reservation.pickup_location.address || '') 
                + ', ' + (reservation.pickup_location.city || '') 
                + ', ' + (reservation.pickup_location.state || ''),
            pickupId: reservation.pickup_location_id,
            pickup_latitude: reservation.pickup_location?.latitude,
            pickup_longitude: reservation.pickup_location?.longitude,
            dropoff: (reservation.dropoff_location.address || '') 
                + ', ' + (reservation.dropoff_location.city || '') 
                + ', ' + (reservation.dropoff_location.state || ''),
            dropoffId: reservation.dropoff_location_id,
            dropoff_latitude: reservation.dropoff_location?.latitude,
            dropoff_longitude: reservation.dropoff_location?.longitude,
            partnerId: reservation.commission_partner_id,
            status: reservation.status,
            notes: reservation.notes,
            customerName: reservation.full_name,
            customerPhone: reservation.phone_number,
            customerEmail: reservation.email,
            price: reservation.amount,
            discount: reservation.discount,
            discountType: reservation.discount_type || "percentage",
        } as CalendarEvent));
    } else {
        console.error("Unexpected response format for reservations:", response.data);
    }

    return events;
};

const addReservation = async (newEvent: any) => {
    var reservationData = {
        pickup_date: newEvent.start.toISOString(),
        dropoff_date: newEvent.end.toISOString(),
        vehicle_id: newEvent.vehicleId,
        commission_partner_id: newEvent.partnerId || 0, // Api requires to set an int value for partner
        pickup_location_id: newEvent.pickupId,
        dropoff_location_id: newEvent.dropoffId,
        full_name: newEvent.customerName,
        email: newEvent.customerEmail,
        phone_number: newEvent.customerPhone,
        amount: newEvent.price,
        discount: newEvent.discount,
        discount_type: newEvent.discountType,
        status: newEvent.status,
        notes: newEvent.notes,
    }

    try {
        const response = await axios.post(`${BASE_URL}/reservations/rental/add-booking`, reservationData, {
            headers: getAuthHeader(),
        });

        return {
            ...newEvent,
            id: response.data.id.toString(), // Ensure id is a string
        } as CalendarEvent;
        
    } catch (err: any) {
        console.error("Error creating reservation:", err);
        toast.error(err.response?.data?.message || "Something went wrong, please try again.");
    }
}

const updateReservation = async (newEvent: CalendarEvent) => {
    var updateReservation = {
        pickup_date: newEvent.start.toISOString(),
        dropoff_date: newEvent.end.toISOString(),
        vehicle_id: newEvent.vehicleId,
        commission_partner_id: newEvent.partnerId || 0, // Api requires to set an int value for partner
        pickup_location_id: newEvent.pickupId,
        dropoff_location_id: newEvent.dropoffId,
        full_name: newEvent.customerName,
        email: newEvent.customerEmail,
        phone_number: newEvent.customerPhone,
        amount: newEvent.price,
        discount: newEvent.discount,
        discount_type: newEvent.discountType,
        status: newEvent.status,
        notes: newEvent.notes,
    };

    return await axios.patch(`${BASE_URL}/reservations/rental/booking/${newEvent.id}`, updateReservation, {
        headers: getAuthHeader(),
    });
}

const deleteReservation = async (reservationId: string) => {
    return await axios.delete(`${BASE_URL}/reservations/rental/booking/${reservationId}`, {
        headers: getAuthHeader(),
    });
}

export {
    getLocations,
    getCommissionPartners,
    getVehicles,
    getAvailableVehicles,
    getReservations,
    getReservationStates,
    addReservation,
    updateReservation,
    deleteReservation
};